import React, { useEffect, useState } from "react";
import {
  Mic, MessageSquareQuote, Layers, Database, Phone, PhoneIncoming, Repeat, ShieldCheck, ChevronDown,
  ListOrdered, BadgeCheck, PhoneCall, Ban, Calculator, Palette, ThumbsUp, BookOpen, Flag,
} from "lucide-react";
import { MISS_AT, scoreBand } from "./callMeta.js";

// How a call becomes a score, in plain words, for the people being scored.
//
// The skill list at the bottom is NOT written out here: it is the live rubric
// (/RevOps/rubric/, the same payload the scorer reads), so a reworded anchor or
// a new parameter shows up on this page the moment it ships, and the page can
// never explain a rubric the scorer is no longer using. Everything above it is
// the pipeline's rules, and each one names where it lives, so a change to the
// rule is a reminder to change the sentence:
//
//   refusals        AXON ECHO/scoreability.py (MIN_SCOREABLE_SECONDS, turn floors)
//   call kind       AXON ECHO/call_kind.py, ingest_views scoring-context
//   voice weight    AXON ECHO/rubric.py VOICE_WEIGHT
//   evidence rules  AXON ECHO/rubric.py, ingest_views.py
//   colours         callMeta.js scoreBand (rendered from it, not copied)
//
// Docs/SCORECARD_IN_PLAIN_ENGLISH.md is the longer version, for the whole page.
//
// Every section starts closed, as a bar with a title and one line saying what's
// inside, so the page reads as a list of questions a rep can pick from rather
// than one long read. The skills open one at a time for the same reason.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };
const h2 = { fontSize: 15, fontWeight: 700, color: "var(--c-900)", margin: "0 0 8px" };
const body = { fontSize: 13, lineHeight: 1.65, color: "var(--c-800)", margin: 0 };

const TRACKS = [
  { key: "junior", label: "SDR" },
  { key: "senior", label: "AE" },
];

const MODALITIES = [
  { key: "call", label: "Phone calls" },
  { key: "meeting", label: "Demos & meetings" },
];

// How each dimension is proved, said the way a rep would say it.
const PROOF = {
  tonality: { label: "Your voice", icon: Mic, color: "var(--blue-600)",
              text: "Scored only from the audio: your pace, pauses, pitch, filler words, and how your "
                    + "voice moved right after the buyer pushed back. The words don't count here." },
  technicality: { label: "Your words", icon: MessageSquareQuote, color: "var(--green-600)",
                  text: "Scored only from what was said. The proof is an actual line from the call, "
                        + "which has to be found in the recording." },
  both: { label: "Voice and words", icon: Layers, color: "var(--amber-600)",
          text: "Needs one proof of each: a line you said and something heard in your voice. How a "
                + "call flows can't be judged from words or tone alone." },
  outcome: { label: "The CRM", icon: Database, color: "var(--c-500)",
             text: "Not scored from the call at all. Whether a meeting happened or a lead was kept "
                   + "up to date only becomes true days later, so it's checked in the CRM once a week." },
};

const STEPS = [
  ["A call with a buyer is recorded.",
   "Only real calls with a buyer count. Practice recordings and internal calls are never scored."],
  ["It's checked first: can this call be scored fairly?",
   "Too short, one-sided, or no real back-and-forth, and it isn't scored at all. See the list below."],
  ["An AI goes through the call once, skill by skill.",
   "It gives each skill on your list a mark out of 10, and for every mark it has to show the proof: "
   + "the line you said, or what was heard in your voice."],
  ["The proof is checked.",
   "If the quote can't be found in the recording, or the proof is the wrong kind for that skill, the "
   + "score is thrown away. No proof, no score."],
  ["It shows up on your scorecard.",
   "Usually a few minutes after the call ends, with the recording, the moments that decided the call, "
   + "and the one thing to work on next."],
];

const NOT_SCORED = [
  ["Shorter than 40 seconds", "A voicemail, a quick hello, or a no-show isn't a performance."],
  ["No real conversation", "Each side needs to speak at least 3 times. A rep talking to silence isn't a call yet."],
  ["You barely spoke", "A call you sat in on is not one you ran."],
  ["Only one side was recorded", "Every measure would come out wrong, so none is taken."],
  ["The two voices couldn't be told apart", "If everything is under one speaker, nothing can fairly be put down to you."],
];

const NEVER_A_FAULT = [
  "The buyer talking more than you. Asking, then listening, is discovery.",
  "Not interrupting. Letting the buyer finish is not losing control.",
  "Agreeing with a fair point from the buyer. Caving is giving up the price, the next step or your "
    + "position under pressure, for nothing.",
  "Greetings and catching up at the start of a callback or a follow-up.",
  "A pause before you answer. A beat of silence is what a 10 sounds like.",
];

// One sample score per colour, so the swatches below come from scoreBand
// itself and can't drift from what every other screen paints.
const BAND_SAMPLES = [
  [8, "7 and above"],
  [6, `above ${MISS_AT}, below 7`],
  [4.5, `4 to ${MISS_AT}`],
  [3, "below 4"],
];
const BAND_MEANING = {
  good: "On target.",
  close: "Not a miss, not there yet.",
  miss: "A miss: work on it.",
  focus: "Fix this first.",
};

// A bar that opens to show its section. The whole bar is the button.
function Section({ id, icon: Icon, title, summary, open, onToggle, children }) {
  const bodyId = `how-scoring-${id}`;
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={bodyId}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
                       background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
                       color: "inherit", font: "inherit" }}>
        <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center",
                       background: "var(--blue-50, rgba(80,120,255,.10))" }}>
          <Icon size={16} color="var(--blue-600)" />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ ...h2, margin: 0 }}>{title}</h2>
          <span style={{ display: "block", fontSize: 12.5, ...muted, marginTop: 2 }}>{summary}</span>
        </span>
        <ChevronDown size={18} color="var(--c-500)"
                     style={{ flexShrink: 0, transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div id={bodyId} style={{ padding: "4px 18px 18px", paddingLeft: "clamp(18px, 6vw, 62px)",
                                  borderTop: "1px solid var(--c-200)" }}>
          <div style={{ paddingTop: 14 }}>{children}</div>
        </div>
      )}
    </div>
  );
}

function Pill({ children, color = "var(--c-500)" }) {
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".03em", padding: "2px 8px",
                   borderRadius: 999, border: `1px solid ${color}`, color, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

// A skill's name, proof and one-line meaning; press it for what a 0 and a 10
// sound like.
function SkillRow({ param, meaning }) {
  const [open, setOpen] = useState(false);
  const proof = PROOF[param.dimension];
  return (
    <div style={{ borderBottom: "1px solid var(--c-200)" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 0",
                       background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
                       color: "inherit", font: "inherit" }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <strong style={{ fontSize: 13.5, color: "var(--c-900)" }}>{param.label}</strong>
            {proof && <Pill color={proof.color}>{proof.label}</Pill>}
            {param.cold_call_only && <Pill>Cold calls only</Pill>}
          </span>
          {meaning && <span style={{ ...body, display: "block", marginTop: 3 }}>{meaning}</span>}
        </span>
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 4, fontSize: 11.5,
                       color: "var(--blue-600)", marginTop: 2, whiteSpace: "nowrap" }}>
          {open ? "Hide" : "0 vs 10"}
          <ChevronDown size={14} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
        </span>
      </button>
      {open && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8,
                      paddingBottom: 12 }}>
          {[["Sounds like a 0", param.zero, "var(--red-600, #c0392b)"],
            ["Sounds like a 10", param.ten, "var(--green-600)"]].map(([label, text, color]) => (
            <div key={label} style={{ borderLeft: `3px solid ${color}`, paddingLeft: 10 }}>
              <div style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".04em" }}>
                {label}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--c-800)" }}>{text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// `ownTrack` is the viewer's own role when it's known (a rep with scored calls),
// and the list opens on it. Both lists stay one click away for everybody: the
// rubric isn't private, and a rep with nothing scored yet has no track to go by.
function SkillList({ rubric, ownTrack }) {
  const [track, setTrack] = useState(ownTrack || "junior");
  useEffect(() => { if (ownTrack) setTrack(ownTrack); }, [ownTrack]);

  if (!rubric) return <p style={{ ...muted, fontSize: 13, margin: 0 }}>Loading the skill list…</p>;
  const byModality = rubric[track]?.parameters_by_modality || {};
  const meanings = rubric.meanings || {};

  return (
    <>
      <p style={{ ...body, marginBottom: 10 }}>
        SDRs and AEs do different jobs, so each has its own list. Phone calls and demos are different crafts
        too, so they're scored separately and never mixed.
      </p>
      <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
        {TRACKS.map((t) => (
          <button key={t.key} onClick={() => setTrack(t.key)} style={{
            border: "1px solid var(--c-200)", cursor: "pointer", fontSize: 12, padding: "5px 12px",
            borderRadius: 7, fontWeight: track === t.key ? 700 : 400,
            background: track === t.key ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
            color: track === t.key ? "var(--c-900)" : "var(--c-500)",
          }}>{t.label}{t.key === ownTrack ? " (yours)" : ""}</button>
        ))}
      </div>
      {MODALITIES.map((m) => (
        <div key={m.key} style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, ...muted, textTransform: "uppercase", letterSpacing: ".05em" }}>
            {m.label}
          </div>
          {(byModality[m.key] || []).map((p) => (
            <SkillRow key={p.key} param={p} meaning={meanings[p.key]} />
          ))}
        </div>
      ))}
    </>
  );
}

// The bars, in page order: what each one is called and the one line under it
// that says what's inside before anybody opens it.
const SECTIONS = {
  steps: { icon: ListOrdered, title: "From call to score",
           summary: "The five steps from a recorded call to your scorecard." },
  proof: { icon: BadgeCheck, title: "What counts as proof",
           summary: "Every score needs proof: your voice, your words, or the CRM." },
  kinds: { icon: PhoneCall, title: "Cold call, callback or follow-up",
           summary: "How the kind of call is decided, and why it changes what's scored." },
  refused: { icon: Ban, title: "Calls that aren't scored",
             summary: "Too short, one-sided, or no real conversation." },
  numbers: { icon: Calculator, title: "How the numbers add up",
             summary: "One call, your last 5 calls, and when a change really counts." },
  colours: { icon: Palette, title: "What the colours mean",
             summary: "Green, blue, amber and red, and where a miss starts." },
  never: { icon: ThumbsUp, title: "Things you're never marked down for",
           summary: "Listening, not interrupting, agreeing with a fair point, pausing." },
  skills: { icon: BookOpen, title: "The skills, and what a 0 and a 10 sound like",
            summary: "Every skill on the list, what it means, and how it's proved." },
  dispute: { icon: Flag, title: "Think a score is wrong?",
             summary: "How to ask your coach to look at a score again." },
};

export default function HowScoringWorks({ rubric, ownTrack }) {
  // The server's own trend rule; the Growth page prints the same two numbers from the same code.
  const trendRules = {
    minCalls: rubric?.trend_rules?.min_calls_for_trend ?? 3,
    floor: rubric?.trend_rules?.resolution_floor ?? 0.5,
  };
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const allOpen = openKeys.size === Object.keys(SECTIONS).length;
  const toggle = (key) => setOpenKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const sec = (key) => ({ id: key, ...SECTIONS[key], open: openKeys.has(key), onToggle: () => toggle(key) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ ...card, background: "var(--blue-50, rgba(80,120,255,.08))", marginBottom: 4 }}>
        <p style={{ ...body, fontSize: 14 }}>
          Every call you have with a buyer is scored against the skills for your role. This page explains
          how that works, in plain words. The short version: <strong>every score has to point to proof from
          the call, and if there's no proof, there's no score.</strong>
        </p>
        <p style={{ ...body, ...muted, fontSize: 12.5, marginTop: 6 }}>
          Press any section below to open it.
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button"
                onClick={() => setOpenKeys(allOpen ? new Set() : new Set(Object.keys(SECTIONS)))}
                style={{ border: "1px solid var(--c-200)", background: "var(--surface)", cursor: "pointer",
                         fontSize: 12, padding: "5px 12px", borderRadius: 7, color: "var(--c-700, var(--c-800))" }}>
          {allOpen ? "Close all" : "Open all"}
        </button>
      </div>

      <Section {...sec("steps")}>
        <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
          {STEPS.map(([title, text], i) => (
            <li key={title} style={{ display: "flex", gap: 12 }}>
              <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 999, display: "grid",
                             placeItems: "center", fontSize: 12, fontWeight: 700, color: "var(--blue-600)",
                             border: "1px solid var(--blue-600)" }}>{i + 1}</span>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--c-900)" }}>{title}</div>
                <p style={{ ...body, ...muted }}>{text}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section {...sec("proof")}>
        <p style={{ ...body, marginBottom: 12 }}>
          Each skill says what kind of proof it needs. The AI can't score a voice skill from a line of text,
          or a words skill from how you sounded.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
          {Object.values(PROOF).map(({ label, icon: Icon, color, text }) => (
            <div key={label} style={{ border: "1px solid var(--c-200)", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <Icon size={15} color={color} />
                <strong style={{ fontSize: 13, color: "var(--c-900)" }}>{label}</strong>
              </div>
              <p style={{ ...body, fontSize: 12.5 }}>{text}</p>
            </div>
          ))}
        </div>
        <p style={{ ...body, marginTop: 12 }}>
          <strong>If something never came up, it stays blank.</strong> If nobody pushed back, you aren't marked
          on handling push-back: you get a dash. A blank is not a zero.
        </p>
      </Section>

      <Section {...sec("kinds")}>
        <p style={{ ...body, marginBottom: 10 }}>
          Every phone call is marked as one of three kinds, and the top of the call card says which. It matters
          because some skills only make sense on a cold call.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
          {[[Phone, "Cold call", "The first time you're speaking to this lead."],
            [PhoneIncoming, "Callback", "The opening says so (\"you'd called me\", \"maine call kiya\"), or the "
              + "buyer greets you by name before you've said it."],
            [Repeat, "Follow-up", "We've spoken to this lead before, on a recorded or scored call."],
          ].map(([Icon, label, text]) => (
            <div key={label} style={{ display: "flex", gap: 8 }}>
              <Icon size={16} color="var(--blue-600)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong style={{ fontSize: 13, color: "var(--c-900)" }}>{label}</strong>
                <p style={{ ...body, fontSize: 12.5 }}>{text}</p>
              </div>
            </div>
          ))}
        </div>
        <p style={{ ...body, marginTop: 10 }}>
          A call is never assumed to be cold. A skill marked <em>cold calls only</em> shows "doesn't apply to
          this kind of call" on a callback or a follow-up, and doesn't count against you.
        </p>
      </Section>

      <Section {...sec("refused")}>
        <p style={{ ...body, marginBottom: 8 }}>
          Some recordings can't be judged fairly, so they aren't scored and don't count toward your numbers.
          The call card says why.
        </p>
        {NOT_SCORED.map(([title, text]) => (
          <div key={title} style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px", padding: "6px 0",
                                    borderBottom: "1px solid var(--c-200)", fontSize: 13 }}>
            <strong style={{ color: "var(--c-900)", minWidth: 220 }}>{title}</strong>
            <span style={{ color: "var(--c-700, var(--c-800))" }}>{text}</span>
          </div>
        ))}
        <p style={{ ...body, marginTop: 10 }}>
          If the call is fine but the audio isn't clear enough to measure a voice, only the voice skills are
          left blank. The rest is still scored.
        </p>
      </Section>

      <Section {...sec("numbers")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <p style={body}>
            <strong>On one call,</strong> each skill gets a mark out of 10. The voice skills are averaged into
            {" "}<em>Voice & delivery</em>, and the words skills into <em>Substance</em>. The <em>Blended</em> score
            {" "}counts voice as 60% and substance as 40%, because two people can say the same words and only one
            of them closes. If one half couldn't be measured, the card says the blend comes from the other half
            alone.
          </p>
          <p style={body}>
            <strong>On your scorecard,</strong> each skill shows the average of your last 5 scored calls. One call
            is noise; five is a pattern. Calls that couldn't be scored don't count as one of the five. The
            number under each skill tells you how many calls it's built from, so a 5.7 from one call and a 5.7
            from five aren't read as the same thing. Skills from the CRM use the last 5 weekly CRM checks.
          </p>
          <p style={body}>
            <strong>A small change isn't a trend.</strong> Growth only says a skill got better or worse when
            there are at least {trendRules.minCalls} calls on each side and it moved by at least {trendRules.floor}. Smaller moves can happen even
            when the same call is scored twice.
          </p>
        </div>
      </Section>

      <Section {...sec("colours")}>
        <p style={{ ...body, marginBottom: 10 }}>
          Anything at {MISS_AT} or below is a <strong>miss</strong>, and the colours follow that line, so a miss
          never looks fine.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
          {BAND_SAMPLES.map(([sample, range]) => {
            const band = scoreBand(sample);
            return (
              <div key={band.key} style={{ display: "flex", gap: 10, alignItems: "center",
                                          border: "1px solid var(--c-200)", borderRadius: 10, padding: "8px 10px" }}>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: band.color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: band.color }}>{range}</div>
                  <div style={{ fontSize: 12, ...muted }}>{BAND_MEANING[band.key]}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section {...sec("never")}>
        <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
          {NEVER_A_FAULT.map((line) => <li key={line} style={body}>{line}</li>)}
        </ul>
      </Section>

      <Section {...sec("skills")}>
        <SkillList rubric={rubric} ownTrack={ownTrack} />
      </Section>

      <Section {...sec("dispute")}>
        <div style={{ display: "flex", gap: 10 }}>
          <ShieldCheck size={18} color="var(--blue-600)" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={body}>
            Open the call, then <strong>Every score on this call</strong>, and press <strong>Dispute</strong> on
            the score. Say what it missed, for example "that line was the buyer's, not mine". Your coach listens
            to the moment and either keeps the score or changes it, and writes you a note either way. A dispute
            never changes a score by itself. If a coach changes a score, the card says so, with their reason,
            and the original score is always kept.
          </p>
        </div>
      </Section>

      <p style={{ ...muted, fontSize: 12.5, lineHeight: 1.6, margin: "2px 4px 0" }}>
        The score isn't the point. "You're stuck" is useless; "your last three calls went red on conviction, and
        here are the exact moments" is something you can work on this week.
      </p>
    </div>
  );
}
